import * as Yup from 'yup';
import {
    Box,
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormControlLabel,
    Grid,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    TextField,
    Typography
} from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import { OpenModalHookType } from '@hooks/useOpenModal';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import { ProfileTypes, UserType } from '@api/Users/User';
import { useSelector } from 'react-redux';
import { RootReducer } from '@store/index';
import { getProfileName } from '@utils/Profiles';
import { canAssignBranches, getAssignableProfiles } from '@utils/Permissions/Capabilities';
import { canCurrentUser, getCurrentPermissionSubject } from '@utils/Permissions';
import { useVisibleBranchOffices } from '@hooks/usePermissionSubject';
import { Capability } from '@utils/Permissions/Capabilities';
import { ModalChangeEmail } from '@components/Admin/Users/Modals/ModalChangeEmail';
import { ModalResetPin } from '@components/Admin/Users/Modals/ModalResetPin';

interface ModalSaveUserProps {
    modalAddUser: OpenModalHookType;
    user?: UserType;
    onAddUser: (addUserType: AddUserType) => void;
    /** Se cambió el correo del usuario editado (la lista debe releerlo). */
    onEmailChanged?: () => void;
}

const saveUserSchema = Yup.object({
    uid: Yup.string(),
    firstname: Yup.string().required('Nombre es requerido'),
    lastname: Yup.string().required('Apellido es requerido'),
    // Sin correo, el server le genera uno interno (`nombre.apellido@staff.krispis.mx`).
    noEmail: Yup.boolean(),
    email: Yup.string().when('noEmail', ([noEmail], schema) =>
        noEmail ? schema.notRequired() : schema.required('Correo es requerido').email('No es un correo válido')
    ),
    phone: Yup.string().required('Número de teléfono es requerido'),
    profile: Yup.string().required('Rol es requerido'),
    branchOffices: Yup.array().min(1, 'Debes de elegir al menos una sucursal').required('Sucursal requerida'),
    address: Yup.string().required('Dirección es requerida'),
    password: Yup.string()
        .required('PIN es requerido')
        .min(6, 'PIN debe de ser de 6 digitos')
        .max(6, 'PIN debe de ser de 6 digitos')
});

export interface AddUserType {
    uid?: string;
    firstname: string;
    lastname: string;
    email: string;
    /** Alta sin correo: el server genera el interno. */
    noEmail?: boolean;
    phone: string;
    profile: string;
    branchOffices: string[];
    address: string;
    password: string;
}

export const ModalSaveUser: React.FunctionComponent<ModalSaveUserProps> = (props: any) => {
    const { modalAddUser, user, onAddUser, onEmailChanged } = props;
    const [changeEmailOpen, setChangeEmailOpen] = useState(false);
    // Las sucursales que ve quien opera (en "Ver como", la del sidebar) más la opción "Todas".
    const branchOfficesCatalog = useVisibleBranchOffices();
    const { branchOfficeSelected, loggedUser } = useSelector((state: RootReducer) => state.auth);
    const [resetPinOpen, setResetPinOpen] = useState(false);
    // Nadie asigna más poder del que tiene (`getAssignableProfiles`). El perfil que
    // ya trae el usuario editado se conserva en la lista para no borrarlo al guardar.
    const assignableProfiles = useMemo(() => {
        const allowed = getAssignableProfiles(getCurrentPermissionSubject());
        const current = user?.profile.profile as ProfileTypes | undefined;
        return current && !allowed.includes(current) ? [...allowed, current] : allowed;
    }, [user]);
    // "Todas" solo la asigna quien las tiene todas (`canAssignBranches`); el admin de
    // sucursal ve solo las suyas (`useVisibleBranchOffices`).
    const branchOffices = useMemo(
        () =>
            canAssignBranches(getCurrentPermissionSubject(), ['all'])
                ? [...branchOfficesCatalog, { id: 'all', name: 'Todas' }]
                : branchOfficesCatalog,
        [branchOfficesCatalog]
    );

    const {
        control,
        handleSubmit,
        reset,
        setValue,
        watch,
        formState: { errors }
    } = useForm<AddUserType>({
        resolver: yupResolver(saveUserSchema)
    });

    const handleClose = () => {
        modalAddUser.toggle();
    };

    const noEmail = watch('noEmail');

    const onSaveUser: SubmitHandler<AddUserType> = async (values) => {
        // `noEmail` es de la pantalla: al server le llega el correo vacío.
        const { noEmail: withoutEmail, ...rest } = values;
        onAddUser(withoutEmail ? { ...rest, email: '' } : rest);
    };

    useEffect(() => {
        if (modalAddUser.modalOpen) {
            reset({
                uid: user?.uid || '',
                firstname: user?.profile.firstname || '',
                lastname: user?.profile.lastname || '',
                email: user?.email || '',
                noEmail: false,
                phone: user?.profile.phone || '',
                // Un alta nace con el MENOR acceso: colaborador de la sucursal en
                // operación. Hasta el 2026-09-27 nacía como Admin con todas las
                // sucursales, y quien no tocaba el selector creaba un admin total.
                profile: user?.profile.profile || ProfileTypes.Collaborator,
                branchOffices: user?.profile.branchOffices || (branchOfficeSelected?.id ? [branchOfficeSelected.id] : []),
                address: user?.profile.address || '',
                password: user ? '123456' : ''
            });
        }
    }, [modalAddUser.modalOpen]);

    return (
        <Dialog
            PaperProps={{
                style: { borderRadius: 15, padding: 4 }
            }}
            open={modalAddUser.modalOpen}
            onClose={handleClose}
            aria-labelledby="modal-save-user-title"
            aria-describedby="modal-save-user-description"
            fullWidth
            maxWidth="md"
        >
            <DialogTitle id="modal-save-user-title">
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="h6" fontWeight={600}>
                        Nuevo usuario
                    </Typography>
                    <IconButton color="secondary" onClick={handleClose}>
                        <CloseIcon />
                    </IconButton>
                </Stack>
            </DialogTitle>
            <DialogContent>
                <form id="add-user-form" onSubmit={handleSubmit(onSaveUser)}>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <Typography variant="subtitle2">Completa los datos del nuevo usuario</Typography>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <Controller
                                name="firstname"
                                control={control}
                                render={({ field: { onChange, value } }) => (
                                    <TextField
                                        fullWidth
                                        required
                                        autoFocus
                                        label="Nombre"
                                        variant="outlined"
                                        autoComplete="firstname"
                                        value={value}
                                        onChange={onChange}
                                        error={!!errors.firstname}
                                        helperText={errors.firstname && errors.firstname.message}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <Controller
                                name="lastname"
                                control={control}
                                render={({ field: { onChange, value } }) => (
                                    <TextField
                                        fullWidth
                                        required
                                        label="Apellido"
                                        variant="outlined"
                                        autoComplete="lastname"
                                        value={value}
                                        onChange={onChange}
                                        error={!!errors.lastname}
                                        helperText={errors.lastname && errors.lastname.message}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <Controller
                                name="email"
                                control={control}
                                render={({ field: { onChange, value } }) => (
                                    <TextField
                                        disabled={!!user || !!noEmail}
                                        fullWidth
                                        required={!noEmail}
                                        label="Correo"
                                        variant="outlined"
                                        autoComplete="email"
                                        value={noEmail ? '' : value}
                                        placeholder={noEmail ? 'Se genera uno interno' : undefined}
                                        onChange={onChange}
                                        error={!!errors.email}
                                        helperText={errors.email && errors.email.message}
                                    />
                                )}
                            />
                            {!user && (
                                <Controller
                                    name="noEmail"
                                    control={control}
                                    render={({ field: { onChange, value } }) => (
                                        <FormControlLabel
                                            control={<Checkbox checked={!!value} onChange={(event) => onChange(event.target.checked)} />}
                                            label="No tiene correo (usar uno interno)"
                                        />
                                    )}
                                />
                            )}
                            {user && canCurrentUser(Capability.UsersManage) && (
                                <Button size="small" onClick={() => setChangeEmailOpen(true)}>
                                    Cambiar correo
                                </Button>
                            )}
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <Controller
                                name="phone"
                                control={control}
                                render={({ field: { onChange, value } }) => (
                                    <TextField
                                        fullWidth
                                        required
                                        label="Número de teléfono"
                                        variant="outlined"
                                        autoComplete="phone"
                                        value={value}
                                        onChange={onChange}
                                        error={!!errors.phone}
                                        helperText={errors.phone && errors.phone.message}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <FormControl fullWidth>
                                <InputLabel id="profile-label">Rol</InputLabel>
                                <Controller
                                    name="profile"
                                    control={control}
                                    render={({ field: { onChange, value } }) => (
                                        <Select
                                            labelId="profile-label"
                                            id="profile"
                                            value={value}
                                            label="Rol"
                                            onChange={onChange}
                                            error={!!errors.profile}
                                        >
                                            {assignableProfiles.map((profile, index) => (
                                                <MenuItem key={index} value={profile}>
                                                    {getProfileName(profile)}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    )}
                                />
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <FormControl fullWidth>
                                <InputLabel id="branch-offices-label">Sucursal(es)</InputLabel>
                                <Controller
                                    name="branchOffices"
                                    control={control}
                                    render={({ field: { onChange, value } }) => (
                                        <Select
                                            labelId="branch-offices-label"
                                            id="branch-offices"
                                            multiple
                                            value={value}
                                            label="Sucursal(es)"
                                            onChange={onChange}
                                            error={!!errors.branchOffices}
                                        >
                                            {branchOffices.map((branchOffice, index) => (
                                                <MenuItem key={index} value={branchOffice.id}>
                                                    {branchOffice.name}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    )}
                                />
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} md={8}>
                            <Controller
                                name="address"
                                control={control}
                                render={({ field: { onChange, value } }) => (
                                    <TextField
                                        fullWidth
                                        required
                                        label="Dirección"
                                        variant="outlined"
                                        autoComplete="off"
                                        value={value}
                                        onChange={onChange}
                                        error={!!errors.address}
                                        helperText={errors.address && errors.address.message}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <Controller
                                name="password"
                                control={control}
                                render={({ field: { onChange, value } }) => (
                                    <TextField
                                        disabled={!!user}
                                        fullWidth
                                        required
                                        type="password"
                                        label="PIN temporal"
                                        variant="outlined"
                                        autoComplete="off"
                                        value={value}
                                        onChange={onChange}
                                        error={!!errors.password}
                                        helperText={errors.password && errors.password.message}
                                        inputProps={{ maxLength: 6, inputMode: 'numeric', pattern: '[0-9]*' }}
                                    />
                                )}
                            />
                            {/* Olvidó su PIN: se lo pone quien lo administra (el propio, en Mi perfil). */}
                            {user && user.uid !== loggedUser?.uid && canCurrentUser(Capability.UsersManage) && (
                                <Button size="small" onClick={() => setResetPinOpen(true)}>
                                    Restablecer PIN
                                </Button>
                            )}
                        </Grid>
                    </Grid>
                </form>
            </DialogContent>
            <DialogActions>
                <Box flex={1} textAlign="center">
                    <Button sx={{ width: 300 }} color="primary" variant="contained" type="submit" form="add-user-form">
                        Guardar
                    </Button>
                </Box>
            </DialogActions>
            {user && (
                <ModalResetPin open={resetPinOpen} onClose={() => setResetPinOpen(false)} user={user} />
            )}
            {user && (
                <ModalChangeEmail
                    open={changeEmailOpen}
                    onClose={() => setChangeEmailOpen(false)}
                    user={user}
                    onChanged={(email) => {
                        setValue('email', email);
                        onEmailChanged?.();
                    }}
                />
            )}
        </Dialog>
    );
};
